import { QP_VALUE } from 'src/app/shared/constants/queryParams';
import { getQueryParams } from 'src/app/utils/queryParams';
import type { CatalogueProduct } from 'src/graphql/generated';
import type {
  ProductSetForAdPosting,
  ProductSetFromMutation,
} from 'src/types/product';

import { isEqualProductFeatures } from './isEqualProductFeatures';

const getAllAvailableProductsFromProductSet = (
  productSet?: ProductSetFromMutation,
): ProductSetForAdPosting['items'] => {
  const productSetItems = (productSet as ProductSetForAdPosting)?.items?.filter(
    (item: ProductSetForAdPosting['items'][0]) => {
      if (!item) return false;
      return ['AdProduct', 'AdProductUpgrade'].includes(item?.__typename);
    },
  );
  return productSetItems;
};

export const getProductByIdOrFeatures = ({
  productSet,
  selectedProductId,
  selectedProductFeatures,
}: {
  productSet?: ProductSetFromMutation;
  selectedProductId?: string;
  selectedProductFeatures?: CatalogueProduct['features'];
}) => {
  // Get all the products from the product set so we can look through them later
  // If no products, then discontinue searching for the relevant product
  const products = getAllAvailableProductsFromProductSet(productSet);
  if (!products) {
    return undefined;
  }

  // If we have a selectedProductId then look through products and
  // find one that matches the same selectedProductI.d
  const selectedProductById = selectedProductId
    ? products.find((product) => product?.id === selectedProductId)
    : undefined;

  if (selectedProductById) {
    return selectedProductById;
  }

  const queryParams = getQueryParams();
  const referrer = queryParams?.referrer;
  const isEnabled = Boolean(
    typeof referrer !== 'undefined' && referrer === QP_VALUE.COPY_JOB,
  );

  // Otherwise search by the features of the product and find one
  // that matches the same product features as we already have selected
  const selectedProductByFeatures = selectedProductFeatures
    ? products.find(
        (product) =>
          product?.__typename === 'AdProduct' &&
          isEqualProductFeatures(
            product.features,
            selectedProductFeatures,
            !isEnabled,
          ),
      )
    : undefined;

  return selectedProductByFeatures;
};
